import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { ThumbsUp, Star, Plus, Send, User } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const EndorsementsPage = () => {
  const { user } = useAuth();
  const [endorsements, setEndorsements] = useState([]);
  const [skills, setSkills] = useState([]);
  const [stats, setStats] = useState({ total: 0, avg_rating: 0, positive_pct: 100 });
  const [showRequest, setShowRequest] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadEndorsements(); }, [user]);

  const loadEndorsements = async () => {
    try {
      const data = await api.getEndorsements();
      setEndorsements(data.endorsements || []);
      setSkills(data.skills || []);
      setStats(data.stats || stats);
    } catch {
      setEndorsements([
        { endorsement_id: '1', from_name: 'Sarah Johnson', from_title: 'Engineering Manager at TechCorp', avatar: 'https://i.pravatar.cc/150?img=1', skill: 'Machine Learning', text: 'Exceptional ML engineer with deep understanding of neural networks.', rating: 5, date: '2 weeks ago' },
        { endorsement_id: '2', from_name: 'Michael Chen', from_title: 'CTO at DataMinds', avatar: 'https://i.pravatar.cc/150?img=2', skill: 'Python Development', text: 'Outstanding Python developer. Clean code, great communication.', rating: 5, date: '1 month ago' },
        { endorsement_id: '3', from_name: 'Emily Rodriguez', from_title: 'Product Manager at StartupXYZ', avatar: 'https://i.pravatar.cc/150?img=3', skill: 'System Design', text: 'Brilliant system architect. Designed scalable solutions.', rating: 5, date: '2 months ago' },
      ]);
      setSkills([{ name: 'Machine Learning', endorsements: 1 }, { name: 'Python Development', endorsements: 1 }, { name: 'System Design', endorsements: 1 }]);
      setStats({ total: 3, avg_rating: 5.0, positive_pct: 100 });
    } finally { setLoading(false); }
  };

  const handleRequestEndorsement = async () => {
    if (!requestEmail) return;
    try { await api.requestEndorsement(requestEmail, requestMessage); } catch {}
    toast({ title: 'Request sent!', description: `Endorsement request sent to ${requestEmail}` });
    setShowRequest(false); setRequestEmail(''); setRequestMessage('');
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <div className="flex items-center justify-between mb-8">
            <div><h1 className="text-3xl font-bold text-gray-900 mb-2">Endorsements</h1><p className="text-gray-600">Skill endorsements from colleagues and managers</p></div>
            <Button onClick={() => setShowRequest(!showRequest)} className="bg-[#0A3900] hover:bg-[#1A5C0A]"><Plus className="w-4 h-4 mr-2" />Request Endorsement</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 text-center"><ThumbsUp className="w-8 h-8 text-[#0A3900] mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.total}</div><div className="text-sm text-gray-600">Total Endorsements</div></Card>
            <Card className="p-6 text-center"><Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.avg_rating}</div><div className="text-sm text-gray-600">Average Rating</div></Card>
            <Card className="p-6 text-center"><div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2"><span className="text-green-600 font-bold text-sm">{stats.positive_pct}%</span></div><div className="text-2xl font-bold text-gray-900 mb-1">Positive</div><div className="text-sm text-gray-600">Endorsement Rate</div></Card>
          </div>

          {/* Request Form */}
          {showRequest && (
            <Card className="p-6 mb-8 border-2 border-[#B8C4AD]">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Request an Endorsement</h3>
              <div className="space-y-4">
                <div><Label>Email Address</Label><Input className="mt-2" type="email" value={requestEmail} onChange={(e) => setRequestEmail(e.target.value)} placeholder="colleague@company.com" /></div>
                <div><Label>Personal Message (optional)</Label><Textarea className="mt-2" rows={3} value={requestMessage} onChange={(e) => setRequestMessage(e.target.value)} placeholder="Hi, would you be willing to endorse my skills?" /></div>
                <Button onClick={handleRequestEndorsement} className="bg-[#0A3900] hover:bg-[#1A5C0A]"><Send className="w-4 h-4 mr-2" />Send Request</Button>
              </div>
            </Card>
          )}

          {/* Skills Summary */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Endorsed Skills</h2>
            <div className="flex flex-wrap gap-3">
              {skills.map((s, i) => (
                <Badge key={i} className="px-4 py-2 text-sm bg-[#E0E8D0] text-[#0A3900]">{s.name} ({s.endorsements})</Badge>
              ))}
            </div>
          </div>

          {/* Endorsements List */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">All Endorsements</h2>
          <div className="space-y-4">
            {endorsements.map(e => (
              <Card key={e.endorsement_id} className="p-6">
                <div className="flex items-start gap-4">
                  {e.avatar ? <img src={e.avatar} alt={e.from_name} className="w-12 h-12 rounded-full" /> : <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center"><User className="w-6 h-6 text-gray-500" /></div>}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div><h3 className="font-semibold text-gray-900">{e.from_name}</h3><p className="text-sm text-gray-600">{e.from_title}</p></div>
                      <div className="flex items-center gap-1">{[...Array(e.rating || 5)].map((_, i) => (<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />))}</div>
                    </div>
                    <Badge className="bg-[#E0E8D0] text-[#0A3900] mb-2">{e.skill}</Badge>
                    <p className="text-gray-700">{e.text}</p>
                    <p className="text-sm text-gray-500 mt-2">{e.date}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EndorsementsPage;
